// Country-aware formatting. The active profile comes from /api/system/locale (see LocaleContext);
// until it loads, and for institutes without a country, India is used so output matches the old
// hardcoded ₹ / en-IN / Asia/Kolkata behaviour exactly.

export const DEFAULT_LOCALE_PROFILE = {
  code: 'IN',
  name: 'India',
  currency_code: 'INR',
  currency_symbol: '₹',
  currency_decimals: 2,
  locale: 'en-IN',
  timezone: 'Asia/Kolkata',
  date_format: 'DD/MM/YYYY',
  phone_code: '+91',
  phone_pattern: '^[6-9]\\d{9}$',
  academic_year_start_month: 4,
  tax_pack: 'in_gst',
  payroll_pack: 'in_statutory',
  payment_gateways: ['razorpay', 'upi', 'stripe', 'cash', 'cheque', 'bank_transfer'],
  id_fields: [{ key: 'aadhaar', label: 'Aadhaar' }, { key: 'pan', label: 'PAN' }],
  bank_fields: [{ key: 'ifsc', label: 'IFSC' }],
  states: [],
};

let activeProfile = DEFAULT_LOCALE_PROFILE;

export function setActiveLocaleProfile(profile) {
  activeProfile = { ...DEFAULT_LOCALE_PROFILE, ...(profile || {}) };
}

export function getActiveLocaleProfile() {
  return activeProfile;
}

/**
 * formatMoney(1234.5) -> "₹1,234.50" for India.
 * options.decimals overrides the currency's decimals (e.g. 0 for dashboard totals);
 * options.symbol=false returns only the number.
 */
// A locale or timezone the browser does not know throws a RangeError; fall back to India
// instead of breaking the page.
function withFallback(profile, fn) {
  try {
    return fn(profile);
  } catch {
    return fn(DEFAULT_LOCALE_PROFILE);
  }
}

export function formatMoney(amount, options = {}, profile = activeProfile) {
  const num = Number(amount || 0);
  return withFallback(profile, (p) => {
    const decimals = options.decimals ?? p.currency_decimals ?? 2;
    const text = num.toLocaleString(p.locale, {
      minimumFractionDigits: options.minDecimals ?? decimals,
      maximumFractionDigits: decimals,
    });
    if (options.symbol === false) return text;
    return num < 0 ? `-${p.currency_symbol}${text.replace('-', '')}` : `${p.currency_symbol}${text}`;
  });
}

export function formatNumber(value, options = {}, profile = activeProfile) {
  return withFallback(profile, (p) => Number(value || 0).toLocaleString(p.locale, options));
}

function toDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value, options = { day: '2-digit', month: 'short', year: 'numeric' }, profile = activeProfile) {
  const d = toDate(value);
  return d ? withFallback(profile, (p) => d.toLocaleDateString(p.locale, { timeZone: p.timezone, ...options })) : '';
}

export function formatDateTime(value, options = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }, profile = activeProfile) {
  const d = toDate(value);
  return d ? withFallback(profile, (p) => d.toLocaleString(p.locale, { timeZone: p.timezone, ...options })) : '';
}

/** Numeric date in the country's date_format, e.g. 31/01/2026, 01/31/2026 or 2026-01-31. */
export function formatShortDate(value, profile = activeProfile) {
  const d = toDate(value);
  if (!d) return '';
  return withFallback(profile, (p) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: p.timezone, day: '2-digit', month: '2-digit', year: 'numeric' })
        .formatToParts(d).map((part) => [part.type, part.value]),
    );
    if (p.date_format === 'MM/DD/YYYY') return `${parts.month}/${parts.day}/${parts.year}`;
    if (p.date_format === 'YYYY-MM-DD') return `${parts.year}-${parts.month}-${parts.day}`;
    return `${parts.day}/${parts.month}/${parts.year}`;
  });
}

/** Local (national) number check, without the country code. */
export function isValidPhone(phone, profile = activeProfile) {
  const digits = String(phone || '').replace(/[\s-]/g, '').replace(new RegExp(`^\\${profile.phone_code}`), '');
  try {
    return new RegExp(profile.phone_pattern).test(digits);
  } catch {
    return digits.length >= 6 && digits.length <= 15;
  }
}

export function hasPaymentGateway(gateway, profile = activeProfile) {
  return (profile.payment_gateways || []).includes(gateway);
}
